import { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";

import { DatabaseContext, UserContext } from "../../../context";
import ViewEventDialog from "../../calendar/ViewEventDialog";

export default function FileEventsDialog(props) {
    const [linkedEvents, setLinkedEvents] = useState([]);
    const { events, calendars, folders } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [viewEventDialogOpen, setViewEventDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState();

    useEffect(() => {
        if (!props.file || !currentUserData) {
            return;
        }

        const visibleEvents = events.filter((event) => {
            if (
                !event.files ||
                !event.files.some((file) => file.key === props.file.key)
            ) {
                return false;
            }

            const calendar = calendars.find(
                (item) => item.key === event.calendar,
            );

            if (!calendar) {
                return false;
            }

            if (calendar.user === currentUserData.key) {
                return true;
            }

            const hub = folders.find((item) => item.key === calendar.hub);

            return Boolean(
                hub &&
                    (hub.managers.includes(currentUserData.key) ||
                        hub.users.includes(currentUserData.key)),
            );
        });

        visibleEvents.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
        setLinkedEvents(visibleEvents);
    }, [events, calendars, folders, props.file, currentUserData]);

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setViewEventDialogOpen(true);
    };

    return (
        <>
            <Dialog
                onClose={props.onClose}
                aria-labelledby="file-events-dialog-title"
                open={props.open}
                fullWidth
                maxWidth="sm"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: "background.paper",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "rgba(0, 149, 61, 0.09)",
                        }}
                    >
                        <CalendarMonthOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="file-events-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Events
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Events linked to {props.file?.displayName}.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close events"
                        onClick={props.onClose}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {linkedEvents.length === 0 ? (
                        <Box
                            sx={{
                                minHeight: 210,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                color: "text.secondary",
                                px: 3,
                            }}
                        >
                            <EventBusyOutlinedIcon
                                sx={{ fontSize: 42, mb: 1.25, opacity: 0.45 }}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{ color: "text.primary", fontWeight: 800 }}
                            >
                                No linked events
                            </Typography>
                            <Typography variant="body2">
                                This file has not been attached to any events
                                you can view.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {linkedEvents.map((event) => (
                                <ButtonBase
                                    key={event.key}
                                    onClick={() => handleEventClick(event)}
                                    sx={{
                                        width: "100%",
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor: "background.paper",
                                        textAlign: "left",
                                        justifyContent: "flex-start",
                                        transition:
                                            "border-color 150ms ease, box-shadow 150ms ease",
                                        "&:hover": {
                                            borderColor:
                                                "rgba(0, 149, 61, 0.35)",
                                            boxShadow:
                                                "0 3px 12px rgba(20, 35, 25, 0.07)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 42,
                                            borderRadius: 1,
                                            mr: 1.5,
                                            flexShrink: 0,
                                            backgroundColor:
                                                event.color || "primary.main",
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 800,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {event.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {event.date
                                                ? dayjs(event.date).format(
                                                      "dddd, D MMMM YYYY",
                                                  )
                                                : "Date not specified"}
                                        </Typography>
                                    </Box>
                                    <ChevronRightIcon
                                        sx={{ color: "text.disabled" }}
                                    />
                                </ButtonBase>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button color="inherit" onClick={props.onClose}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ViewEventDialog
                open={viewEventDialogOpen}
                onClose={() => setViewEventDialogOpen(false)}
                event={selectedEvent}
                fromFile={props.file}
            />
        </>
    );
}
